import type { Company, InvoiceDiscount } from "@prisma/client";
import route from "./route";
import buildParams from "~/kernel/searchParams/buildParams";
import SubscriptionId from "~/models/subscription/SubscriptionId";
import type determineBookingEligibility from "~/companyBooking/determineBookingEligibility";
import { z } from "zod";
import CHECKBOX_TRUE_VALUE from "~/constants/form/CHECKBOX_TRUE_VALUE";
import CHECKBOX_FALSE_VALUE from "~/constants/form/CHECKBOX_FALSE_VALUE";

const EMBED_ROUTES = {
  embed: {
    index: route.fromTemplate("/embed", {
      query: z.object({ to: z.string() }),
    }),
  },
};

const BLOG_ROUTES = {
  blogPosts: route("/blog"),
  blogPost: route(
    ({ slug, commentId }: { slug: string; commentId?: string }) =>
      `/blog/${slug}${buildParams({ commentId })}`,
  ),
  blogPostNewComment: route(
    ({ slug, replyToCommentId }: { slug: string; replyToCommentId?: string }) =>
      `/blog/${slug}/comments/new${buildParams({ replyToCommentId })}`,
  ),
};

const MAGIC_LINK_ROUTES = {
  signIn: route.fromTemplate("/magic-links/:token/sign-in", {
    params: z.object({ token: z.string() }),
    query: z.object({ redirectUrl: z.string() }),
  }),
};

const COMPANY_ROUTES = {
  bookingTerms: route(
    ({ slug }: Pick<Company, "slug">) => `/companies/${slug}/booking-terms`,
  ),
  companies: route(
    (query?: string) => `/companies?index${query ? `&query=${query}` : ""}`,
  ),
  company: route.fromTemplate("/companies/:slug", {
    params: z.object({ slug: z.string() }),
    query: z.object({
      page: z.coerce.number().optional(),
    }),
  }),
  companyCancelledSubscription: route("/company-cancelled-subscription"),
  companyBookingSignIn: route(
    ({ slug }: Pick<Company, "slug">) => `/companies/${slug}/bookings/sign-in`,
  ),
  companyNotFound: route(
    ({ slug }: Pick<Company, "slug">) => `/companies/${slug}/not-found`,
  ),
  newBooking: route.fromTemplate("/companies/:slug/bookings/new", {
    params: z.object({
      slug: z.string(),
    }),
    query: z.object({
      claimCustomer: z.union([
        z.literal(CHECKBOX_TRUE_VALUE),
        z.literal(CHECKBOX_FALSE_VALUE),
      ]),
    }),
  }),
  newReview: route.fromTemplate("/companies/:slug/reviews/new", {
    params: z.object({ slug: z.string() }),
    query: z.object({ bookingId: z.string().optional() }),
  }),
  reviewThread: route.fromTemplate("/companies/:slug/reviews/:reviewId", {
    params: z.object({ slug: z.string(), reviewId: z.string() }),
  }),
};

const BOOKING_ROUTES = {
  bookingNotEligible: route(
    ({
      slug,
      reason,
    }: Pick<Company, "slug"> &
      Pick<ReturnType<typeof determineBookingEligibility>, "reason">) => {
      return `/companies/${slug}/bookings/not-eligible/${reason}`;
    },
  ),
};

const REPRESENATION_ROUTES = {
  changeRepresentation: route(
    ({ redirectUrl }: { redirectUrl: string }) =>
      `/representation/change${buildParams({ redirectUrl })}`,
  ),
};

const ANALYTICS_ROUTES = {
  analyticsEvents: route("/analytics/events"),
};

const PASSWORD_RESET_ROUTES = {
  passwordReset: {
    edit: route((token: string) => `/password-resets/${token}`),
    new: route.fromTemplate("/password-resets/new", {
      query: z.object({
        email: z.string().email().optional().catch(undefined),
      }),
    }),
    confirmation: route.fromTemplate(
      "/password-resets/by-email/:email/confirmation",
      { params: z.object({ email: z.string().email() }) },
    ),
  },
};

const PUBLIC_ROUTES = {
  ...ANALYTICS_ROUTES,
  ...COMPANY_ROUTES,
  ...BOOKING_ROUTES,
  ...REPRESENATION_ROUTES,
  ...BLOG_ROUTES,
  ...PASSWORD_RESET_ROUTES,
  ...EMBED_ROUTES,
  magicLink: MAGIC_LINK_ROUTES,
  activeSystemMessage: route("/system-messages/active/json"),
  unansweredUserSurveys: route("/user/surveys/unanswered"),
  newSurveySubmission: route.fromTemplate("/surveys/:id/submissions/new", {
    params: z.object({ id: z.string() }),
    query: z.object({ redirectUrl: z.string() }),
  }),
  dismissSurvey: route.fromTemplate("/surveys/:id/dismiss", {
    params: z.object({ id: z.string() }),
  }),
  companySignUp: route.fromTemplate("/company-sign-up", {
    query: z.object({
      discountCode: z.string().optional(),
      subscriptionId: z.nativeEnum(SubscriptionId).optional().catch(undefined),
      redirectUrl: z.string().optional(),
    }),
  }),
  startupSignUp: route.fromTemplate("/uppstart/:discountCode", {
    params: z.object({ discountCode: z.string() }),
  }),
  connectCompany: route.fromTemplate("/connect-company", {
    query: z.object({ discountCode: z.string().optional() }),
  }),
  discountCodeShortLink: route.fromTemplate("/kod/:discountCode", {
    params: z.object({ discountCode: z.string() }),
  }),
  customerSignUp: route.fromTemplate("/customer-sign-up", {
    query: z.object({ redirectUrl: z.string().optional() }),
  }),
  discount: route(
    ({ discountCode }: Pick<InvoiceDiscount, "discountCode">) =>
      `/invoice-discounts/${discountCode}`,
  ),
  discountValidity: route(
    ({ discountCode }: Pick<InvoiceDiscount, "discountCode">) =>
      `/invoice-discounts/${discountCode}/validity`,
  ),
  help: route("/help"),
  home: route("/home"),
  login: route(
    ({ redirectUrl }: { redirectUrl?: string } = {}) =>
      `/login${buildParams({ redirectUrl })}`,
  ),
  logout: route.fromTemplate("/logout", {
    query: z.object({ redirectUrl: z.string().optional() }),
  }),
  pickAccountType: route(
    ({ redirectUrl }: { redirectUrl?: string } = {}) =>
      `/pick-account-type${buildParams({ redirectUrl })}`,
  ),
  root: route("/"),
  signUp: route("/sign-up"),
  sitemap: route("/sitemap.xml"),
  subscriptions: route("/subscriptions"),
  termsOfUsage: route("/terms-of-usage"),
};

export default PUBLIC_ROUTES;
